import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Upload, X, FileText, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { uploadRegistrationFile } from '@/api/registration';
import type { FileUploadProgress, FileUploadResponse, FileUploadCallback } from '@/types/survey';

const VALID_FILE_TYPES = new Set([
  'IDFILE', 'Gradyation_file', 'SHCC_file', 'IBAN_file',
  'personal_photo', 'MLI_file', 'extra_file',
]);

function resolveFileType(questionCode: string): string {
  if (VALID_FILE_TYPES.has(questionCode)) return questionCode;
  return 'extra_file';
}

export type FileUploaderFn = (
  file: File,
  questionCode: string,
  onProgress?: FileUploadCallback,
) => Promise<FileUploadResponse>;

interface FileUploadFieldProps {
  /** Question code used as the file type identifier */
  questionCode: string;
  /** Whether multiple files are allowed */
  multiple?: boolean;
  /** Accepted file types (e.g., 'image/*,.pdf') */
  accept?: string;
  /** Maximum file size in MB */
  maxSizeMB?: number;
  /** Current value (file ID(s) already uploaded) */
  value?: string | string[];
  /** Callback when file(s) are uploaded, returns file_id(s) */
  onChange: (fileIds: string | string[]) => void;
  /** Locale for labels */
  locale?: string;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Optional custom upload function — overrides the default registration uploader */
  uploadFn?: FileUploaderFn;
}

interface UploadedFile {
  fileId: string;
  filename: string;
  size: number;
}

type UploadStatus = 'idle' | 'uploading' | 'success' | 'error';

export const FileUploadField: React.FC<FileUploadFieldProps> = ({
  questionCode,
  multiple = false,
  accept = 'image/*,.pdf,.doc,.docx',
  maxSizeMB = 10,
  value,
  onChange,
  locale = 'en',
  disabled = false,
  uploadFn,
}) => {
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>('idle');
  const [progress, setProgress] = useState<FileUploadProgress>({ loaded: 0, total: 0, percentage: 0 });
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isAr = locale === 'ar';

  useEffect(() => {
    if (value === undefined || value === '' || (Array.isArray(value) && value.length === 0)) {
      setUploadedFiles([]);
      setUploadStatus('idle');
      setErrorMessage('');
      return;
    }
    const ids = (Array.isArray(value) ? value : [value]).filter(
      (id): id is string => typeof id === 'string' && id.length > 0,
    );
    setUploadedFiles((prev) => {
      if (
        ids.length === prev.length &&
        ids.every((id, i) => prev[i]?.fileId === id)
      ) {
        return prev;
      }
      return ids.map((fileId) => ({ fileId, filename: '', size: 0 }));
    });
    if (ids.length > 0) setUploadStatus('success');
  }, [value]);

  const handleFileSelect = useCallback(
    async (files: FileList | null) => {
      if (!files || files.length === 0) return;

      const filesToUpload = multiple ? Array.from(files) : [files[0]];

      // Validate file sizes
      for (const file of filesToUpload) {
        if (file.size > maxSizeMB * 1024 * 1024) {
          setErrorMessage(
            isAr
              ? `الملف كبير جداً. الحد الأقصى ${maxSizeMB} ميجابايت`
              : `File too large. Maximum size is ${maxSizeMB}MB`
          );
          setUploadStatus('error');
          return;
        }
      }

      setUploadStatus('uploading');
      setErrorMessage('');

      const newUploadedFiles: UploadedFile[] = [...uploadedFiles];

      try {
        const doUpload = uploadFn
          ? (f: File) => uploadFn(f, questionCode, (p) => setProgress(p))
          : (f: File) => uploadRegistrationFile(f, resolveFileType(questionCode), (p) => setProgress(p));

        for (const file of filesToUpload) {
          const result = await doUpload(file);

          newUploadedFiles.push({
            fileId: result.file_id,
            filename: file.name,
            size: file.size,
          });
        }

        setUploadedFiles(newUploadedFiles);
        setUploadStatus('success');

        // Report back file IDs
        const fileIds = newUploadedFiles.map((f) => f.fileId);
        onChange(multiple ? fileIds : fileIds[0]);
      } catch (err) {
        setUploadStatus('error');
        setErrorMessage(
          err instanceof Error
            ? err.message
            : isAr
              ? 'فشل رفع الملف'
              : 'File upload failed'
        );
      }
    },
    [questionCode, multiple, maxSizeMB, uploadedFiles, onChange, isAr, uploadFn]
  );

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);
      handleFileSelect(e.dataTransfer.files);
    },
    [handleFileSelect]
  );

  const removeFile = useCallback(
    (index: number) => {
      const newFiles = uploadedFiles.filter((_, i) => i !== index);
      setUploadedFiles(newFiles);
      const fileIds = newFiles.map((f) => f.fileId);
      onChange(multiple ? fileIds : fileIds[0] || '');
      if (newFiles.length === 0) {
        setUploadStatus('idle');
      }
    },
    [uploadedFiles, multiple, onChange]
  );

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="space-y-3">
      {/* Drop zone */}
      <div
        className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer ${
          dragActive
            ? 'border-primary bg-primary/5'
            : disabled
              ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
              : 'border-gray-300 hover:border-primary/50 hover:bg-gray-50'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => !disabled && fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          accept={accept}
          multiple={multiple}
          disabled={disabled}
          onChange={(e) => handleFileSelect(e.target.files)}
        />

        {uploadStatus === 'uploading' ? (
          <div className="space-y-3">
            <Loader2 className="h-8 w-8 mx-auto text-primary animate-spin" />
            <p className="text-sm text-muted-foreground">
              {isAr ? 'جاري الرفع...' : 'Uploading...'}
            </p>
            <Progress value={progress.percentage} className="h-2 max-w-xs mx-auto" />
            <p className="text-xs text-muted-foreground">{progress.percentage}%</p>
          </div>
        ) : (
          <div className="space-y-2">
            <Upload className="h-8 w-8 mx-auto text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              {isAr
                ? 'اسحب وأفلت ملفاً هنا، أو انقر للتصفح'
                : 'Drag and drop a file here, or click to browse'}
            </p>
            <p className="text-xs text-muted-foreground">
              {isAr ? `أقصى حجم: ${maxSizeMB} ميجابايت` : `Max size: ${maxSizeMB}MB`}
            </p>
          </div>
        )}
      </div>

      {/* Error message */}
      {uploadStatus === 'error' && errorMessage && (
        <div className="flex items-center gap-2 text-destructive text-sm">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <span>{errorMessage}</span>
        </div>
      )}

      {/* Uploaded files list */}
      {uploadedFiles.length > 0 && (
        <div className="space-y-2">
          {uploadedFiles.map((file, index) => (
            <div
              key={file.fileId}
              className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg"
            >
              <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
              <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{file.filename}</p>
                <p className="text-xs text-muted-foreground">
                  {formatFileSize(file.size)}
                </p>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                onClick={(e) => {
                  e.stopPropagation();
                  removeFile(index);
                }}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
